import {
  CLUSTERS,
  EXTRA_CLUSTER,
  FUNCTION_CLUSTERS,
  NEXT_TOKEN_RULES,
  PRESETS,
  TOKEN_COLORS,
  type ClusterId,
  type HeadId,
  type Vec3,
} from '../data/content'

export type WordInfo = {
  display: string
  norm: string
  pos: Vec3
  cluster: ClusterId
}

export type Candidate = { text: string; logit: number }

export function normalizeWord(text: string) {
  return text.trim().toLowerCase()
}

export const CLOUD_WORDS: WordInfo[] = CLUSTERS.flatMap((cluster) =>
  cluster.words.map((word, i) => ({
    display: word,
    norm: normalizeWord(word),
    pos: add(
      cluster.center,
      fibonacciPoint(i, cluster.words.length, 1.6, hashString(word)),
    ),
    cluster: cluster.id,
  })),
)

const WORD_BY_NORM = new Map(CLOUD_WORDS.map((w) => [w.norm, w]))
const CLUSTER_BY_ID = new Map(CLUSTERS.map((c) => [c.id, c]))
const KNOWN_IDS = new Map(PRESETS.flatMap((p) => p.tokens))

// Fixed random directions that project a meaning-space position into the
// 8 displayed numbers, so nearby words get similar vectors.
const PROJECTION: Vec3[] = (() => {
  const rand = seeded(20240917)
  return Array.from({ length: 8 }, () => randomUnit(rand))
})()

/** Where a token sits in meaning space, relative to the cloud's center. */
export function wordInfo(text: string): WordInfo {
  const norm = normalizeWord(text)
  const known = WORD_BY_NORM.get(norm)
  if (known) return known
  const cluster = EXTRA_CLUSTER[norm] ?? 'grammar'
  const center = CLUSTER_BY_ID.get(cluster)?.center ?? [0, 0, 0]
  const rand = seeded(hashString(norm))
  const dir = randomUnit(rand)
  const r = 0.5 + rand() * 0.6
  return {
    display: text.trim(),
    norm,
    pos: [center[0] + dir[0] * r, center[1] + dir[1] * r * 0.7, center[2] + dir[2] * r],
    cluster,
  }
}

export function embeddingFor(text: string): number[] {
  const { pos } = wordInfo(text)
  const rand = seeded(hashString(text) ^ 0x9e3779b9)
  return PROJECTION.map((axis) =>
    Math.tanh(0.42 * dot(pos, axis) + (rand() - 0.5) * 0.7),
  )
}

/** Causal attention from token `i` to itself and every earlier token. */
export function attentionWeights(texts: string[], i: number, head: HeadId) {
  const query = wordInfo(texts[i])
  const scores: number[] = []
  for (let j = 0; j <= i; j++) {
    if (head === 'previous') {
      scores.push(j === i - 1 ? 3.2 : j === i ? 1.2 : -0.1 * (i - j))
    } else if (head === 'first') {
      const noise = seeded(hashString(texts[j]) + i * 31)()
      scores.push(j === 0 ? 3.0 : (j === i ? 0.6 : 0) + 0.25 * noise)
    } else {
      const key = wordInfo(texts[j])
      const closeness = Math.exp(-distSq(query.pos, key.pos) / 26)
      const content = !FUNCTION_CLUSTERS.includes(key.cluster)
      const score = content ? 1.4 + 1.2 * closeness : 0.4 * closeness
      scores.push(1.5 * (score + (j === i ? 0.3 : 0)))
    }
  }
  return softmax(scores, 1)
}

export function candidatesFor(texts: string[]): Candidate[] {
  const sentence = texts.join('')
  const rule =
    NEXT_TOKEN_RULES.find((r) => r.match(sentence)) ??
    NEXT_TOKEN_RULES[NEXT_TOKEN_RULES.length - 1]
  return rule.candidates.map(([text, logit]) => ({ text, logit }))
}

export function softmax(values: number[], temperature: number) {
  const scaled = values.map((v) => v / Math.max(temperature, 0.01))
  const max = Math.max(...scaled)
  const exps = scaled.map((v) => Math.exp(v - max))
  const sum = exps.reduce((a, b) => a + b, 0)
  return exps.map((e) => e / sum)
}

export function sampleIndex(probs: number[], r = Math.random()) {
  let acc = 0
  for (let i = 0; i < probs.length; i++) {
    acc += probs[i]
    if (r < acc) return i
  }
  return probs.length - 1
}

export function tokenIdFor(text: string) {
  return KNOWN_IDS.get(text) ?? 1000 + (hashString(text) % 48000)
}

export function nearestWords(text: string, count: number): WordInfo[] {
  const self = wordInfo(text)
  return CLOUD_WORDS.filter((w) => w.norm !== self.norm)
    .map((w) => ({ w, d: distSq(w.pos, self.pos) }))
    .sort((a, b) => a.d - b.d)
    .slice(0, count)
    .map((x) => x.w)
}

export function tokenColor(index: number) {
  return TOKEN_COLORS[index % TOKEN_COLORS.length]
}

const ZERO_RGB = [0x1c, 0x1f, 0x2b]
const POS_RGB = [0xf5, 0x9e, 0x0b]
const NEG_RGB = [0x3b, 0x82, 0xf6]

/** Blue for negative, near-black for zero, amber for positive. */
export function valueColor(v: number) {
  const t = Math.pow(Math.min(1, Math.abs(v)), 0.75)
  const to = v >= 0 ? POS_RGB : NEG_RGB
  const c = ZERO_RGB.map((z, i) => Math.round(z + (to[i] - z) * t))
  return `rgb(${c[0]}, ${c[1]}, ${c[2]})`
}

export function displayToken(text: string) {
  const body = text.replace(/^ /, '')
  return { space: text.startsWith(' '), body, punctuation: /^[^\w\s]+$/.test(body) }
}

export function formatPct(p: number) {
  return p < 0.01 ? '<1%' : `${Math.round(p * 100)}%`
}

export function hashString(s: string): number {
  let h = 2166136261
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

export function seeded(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function fibonacciPoint(i: number, n: number, radius: number, seed: number): Vec3 {
  const y = n === 1 ? 0 : 1 - ((i + 0.5) / n) * 2
  const r = Math.sqrt(1 - y * y)
  const theta = i * 2.399963 + (seeded(seed)() - 0.5) * 0.5
  return [Math.cos(theta) * r * radius, y * radius * 0.95, Math.sin(theta) * r * radius]
}

function randomUnit(rand: () => number): Vec3 {
  const z = rand() * 2 - 1
  const theta = rand() * Math.PI * 2
  const r = Math.sqrt(1 - z * z)
  return [r * Math.cos(theta), z, r * Math.sin(theta)]
}

function add(a: Vec3, b: Vec3): Vec3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]]
}

function dot(a: Vec3, b: Vec3) {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
}

function distSq(a: Vec3, b: Vec3) {
  const dx = a[0] - b[0]
  const dy = a[1] - b[1]
  const dz = a[2] - b[2]
  return dx * dx + dy * dy + dz * dz
}
